'use client'

import React from 'react'
import { friendlyTxError } from '@/utils/friendly-tx-error'
import axios from 'axios'
import { ArrowRight, Check, Loader2, X } from 'lucide-react'
import Wallet from '@/utils/wallet'
import useWallet from '@/utils/hooks/useWallet'
import useFeeEstimates from '@/utils/hooks/useFeeEstimates'
import { API_URL } from '@/utils/constants'
import { useToast } from '@/components/ui/use-toast'
import Seed from '@/components/seed'
import { normalizeMnemonic } from '@/utils/mnemonic'
import {
	clearRecoveryContext,
	readRawRecoveryContext,
	readRecoveryContext,
	type RecoveryContext
} from '@/utils/recovery/context'

const subscribeNothing = () => () => {}

/**
 * After the recovery page opened an oddly spelled wallet, offer the one
 * thing left to do: move everything to the customer's normal wallet. Two
 * transactions at most — every inscription in one, then the BTC — signed
 * here with the keys already in the browser. The panel only appears when
 * the signed-in wallet is the recovered one, and goes away once both legs
 * are done or the customer dismisses it.
 */

const btc = (sats: number) => `${(sats / 1e8).toFixed(8).replace(/0+$/, '').replace(/\.$/, '')} BTC`
const short = (a: string) => `${a.slice(0, 10)}…${a.slice(-6)}`
const ADDRESS = /^(bc1[a-z0-9]{25,90}|[13][a-km-zA-HJ-NP-Z1-9]{25,40})$/

type Leg = 'idle' | 'working' | 'done' | 'waiting' | 'error'

export default function RecoverySweepPanel() {
	// sessionStorage through a snapshot: null on the server and on first
	// paint, so the panel never differs between server and client markup.
	const raw = React.useSyncExternalStore(subscribeNothing, readRawRecoveryContext, () => null)
	const [dismissed, setDismissed] = React.useState(false)
	const ctx = React.useMemo<RecoveryContext | null>(() => {
		if (!raw || dismissed) return null
		const saved = readRecoveryContext()
		if (!saved) return null
		try {
			return Wallet.getTaprootAddress() === saved.from ? saved : null
		} catch {
			return null
		}
	}, [raw, dismissed])
	const [to, setTo] = React.useState(() => readRecoveryContext()?.to || '')
	const [keepPhrase, setKeepPhrase] = React.useState<string | null>(null)
	const [revealing, setRevealing] = React.useState(false)
	const [editing, setEditing] = React.useState(false)
	const [inscLeg, setInscLeg] = React.useState<Leg>('idle')
	const [btcLeg, setBtcLeg] = React.useState<Leg>('idle')
	const [txids, setTxids] = React.useState<string[]>([])
	const [message, setMessage] = React.useState<string | null>(null)
	const { data: wallet, mutate } = useWallet()
	const { feeRate } = useFeeEstimates()
	const { toast } = useToast()

	if (!ctx) return null

	// The phrase to keep is the standard spelling of the one in this browser:
	// lowercase, single spaces — exactly what opens the destination wallet.
	// Reading it asks for the password, like the settings page does.
	const revealKeepPhrase = async () => {
		setRevealing(true)
		try {
			const { seed } = await Wallet.seed()
			setKeepPhrase(normalizeMnemonic(seed))
		} catch {
			setMessage('Could not read the phrase. Unlock the wallet and try again.')
		} finally {
			setRevealing(false)
		}
	}

	const inscriptionIds: string[] = (wallet?.inscriptions || [])
		.map((i: any) => i.id)
		.filter(Boolean)
	const balance = Number(wallet?.balance) || 0
	const confirmed = Number(wallet?.confirmed_balance ?? wallet?.balance) || 0
	const utxoCount = Number(wallet?.utxo_count) || 1
	const validTo = ADDRESS.test(to.trim())
	const busy = inscLeg === 'working' || btcLeg === 'working'
	const nothingLeft = inscriptionIds.length === 0 && balance <= 0

	const broadcast = async (rawtx: string): Promise<string> => {
		const { data } = await axios.post(`${API_URL}/wallet/broadcast`, { rawtx })
		if (data?.error) {
			const error = new Error(data.error.message || 'Broadcast failed')
			error.name = 'BroadcastError'
			throw error
		}
		return String(data?.txid || data?.result || '')
	}

	// The node refusing a transaction because one of its coins is already
	// spent by a transaction in the mempool (here: the inscription leg that
	// just went out, whose fee coin the API's per-block spendable cache still
	// lists). Rebuilding will pick the same coin; the only fix is a block.
	const spendsPendingCoin = (e: unknown): boolean => {
		const text = String((e as Error)?.message || '').toLowerCase()
		return (
			(e as Error)?.name === 'BroadcastError' &&
			/replacement|conflict|missingorspent|already spent|insufficient fee/.test(text)
		)
	}

	const moveInscriptions = async () => {
		if (inscriptionIds.length === 0) {
			setInscLeg('done')
			return
		}
		setInscLeg('working')
		const psbt = await Wallet.getTransfers(inscriptionIds, to.trim(), feeRate, [])
		const rawtx = await Wallet.signPSBTExtractTx(psbt)
		const txid = await broadcast(rawtx)
		setTxids((t) => [...t, txid])
		setInscLeg('done')
	}

	// The send endpoint takes an exact amount. Start from the confirmed
	// balance minus a fee estimate and step down if the builder says it is
	// short; unconfirmed change (from the inscription leg) is not spendable
	// yet, so that case parks the leg as "waiting" instead of failing.
	const moveBtc = async (inscriptionsJustMoved: boolean) => {
		setBtcLeg('working')
		const spendable = confirmed
		if (spendable <= 0) {
			setBtcLeg(balance > 0 ? 'waiting' : 'done')
			return
		}
		// The inscription transfer just spent a coin the API will still offer
		// for a few seconds; give its mempool view a moment before building.
		if (inscriptionsJustMoved) await new Promise((r) => setTimeout(r, 6000))
		const estimate = Math.ceil(feeRate * (11 + 58 * utxoCount + 43))
		let sats = spendable - estimate
		for (let attempt = 0; attempt < 6 && sats > 546; attempt++) {
			try {
				const {
					data: { psbt }
				} = await axios.post(`${API_URL}/wallet/send`, {
					from: Wallet.getTaprootAddress(),
					to: to.trim(),
					sats,
					public_key: Wallet.getPublicKey(),
					fee_rate: feeRate
				})
				const rawtx = await Wallet.signPSBTExtractTx(psbt)
				const txid = await broadcast(rawtx)
				setTxids((t) => [...t, txid])
				setBtcLeg('done')
				return
			} catch (e: any) {
				if (spendsPendingCoin(e)) {
					// Not an amount problem: the coins are tied up in the transfer
					// that just went out. Park the leg; the next click after the
					// block lands sends the BTC.
					setBtcLeg('waiting')
					return
				}
				const text = String(e?.response?.data?.message || e?.message || '').toLowerCase()
				if (e?.name !== 'BroadcastError' && /insufficient|not enough|exceeds|short/.test(text)) {
					sats -= Math.max(500, Math.ceil(estimate / 2))
					continue
				}
				throw e
			}
		}
		setBtcLeg(balance > 0 ? 'waiting' : 'done')
	}

	const moveEverything = async () => {
		setMessage(null)
		try {
			const inscriptionsJustMoved = inscLeg !== 'done' && inscriptionIds.length > 0
			if (inscLeg !== 'done') await moveInscriptions()
			await moveBtc(inscriptionsJustMoved)
			void mutate()
			toast({ title: 'On its way', description: 'Your assets are moving to your normal wallet.' })
		} catch (e: any) {
			setMessage(friendlyTxError(e))
			if (inscLeg === 'working') setInscLeg('error')
			if (btcLeg === 'working') setBtcLeg('error')
		}
	}

	const finished = inscLeg === 'done' && btcLeg === 'done'

	return (
		<section
			aria-label="Move everything to your normal wallet"
			className="mx-0 mb-4 border-y md:border border-violet-500/40 bg-violet-500/[0.06] md:rounded-lg md:mx-4 lg:mx-0"
		>
			<div className="flex flex-col gap-3 p-4 md:p-5">
				<div className="flex items-start justify-between gap-3">
					<div className="min-w-0">
						<p className="text-[11px] font-semibold text-violet-500">
							Recovered wallet
						</p>
						<h2 className="mt-0.5 text-base font-semibold text-foreground">
							{finished ? 'Everything is on its way' : 'Move everything to your normal wallet'}
						</h2>
						<p className="mt-1 text-sm leading-relaxed text-muted-foreground">
							This wallet was opened by the phrase typed with {ctx.spelling.toLowerCase()}. Any
							normal restore opens a different one, so send your assets there once and use that
							wallet from now on.
						</p>
					</div>
					<button
						type="button"
						aria-label="Dismiss"
						onClick={() => {
							clearRecoveryContext()
							setDismissed(true)
						}}
						className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
					>
						<X className="size-4" />
					</button>
				</div>

				<dl className="grid grid-cols-1 gap-2 rounded-lg border border-border bg-background/60 p-3 text-sm sm:grid-cols-[auto_1fr] sm:gap-x-4">
					<dt className="text-muted-foreground">Moving</dt>
					<dd className="font-medium tabular-nums text-foreground">
						{inscriptionIds.length} inscription{inscriptionIds.length === 1 ? '' : 's'} ·{' '}
						{btc(balance)}
					</dd>
					<dt className="text-muted-foreground">To</dt>
					<dd className="min-w-0">
						{editing ? (
							<input
								value={to}
								onChange={(e) => setTo(e.target.value)}
								spellCheck={false}
								autoCapitalize="none"
								className="w-full rounded-md border border-border bg-background px-2 py-1 font-mono text-xs text-foreground"
								placeholder="bc1p…"
							/>
						) : (
							<span className="flex flex-wrap items-center gap-x-2 gap-y-1">
								<span className="font-mono text-xs text-foreground">{short(to)}</span>
								{to === ctx.to && (
									<span className="text-xs text-muted-foreground">your normal wallet</span>
								)}
								<button
									type="button"
									onClick={() => setEditing(true)}
									className="text-xs text-violet-500 underline-offset-2 hover:underline"
								>
									Use a different address
								</button>
							</span>
						)}
					</dd>
					<dt className="text-muted-foreground">Network fee</dt>
					<dd className="text-foreground">{feeRate} sat/vB · up to two transactions</dd>
				</dl>

				{(inscLeg !== 'idle' || btcLeg !== 'idle') && (
					<ol className="flex flex-col gap-1 text-xs">
						<Step label={`Inscriptions (${inscriptionIds.length})`} leg={inscLeg} />
						<Step label={`Bitcoin (${btc(balance)})`} leg={btcLeg} />
					</ol>
				)}
				{btcLeg === 'waiting' && (
					<p className="text-xs leading-relaxed text-muted-foreground">
						The bitcoin is waiting for the inscription transaction to confirm (about one block).
						Come back to this page and press{' '}
						<span className="font-medium text-foreground">Move the bitcoin</span>.
					</p>
				)}
				{message && <p className="text-xs text-red-500">{message}</p>}
				{txids.length > 0 && (
					<p className="text-xs text-muted-foreground">
						{txids.map((id) => (
							<a
								key={id}
								href={`/tx/${id}`}
								className="mr-3 font-mono text-violet-500 underline-offset-2 hover:underline"
							>
								{id.slice(0, 10)}…
							</a>
						))}
					</p>
				)}

				{(finished || to !== ctx.to) && (
					<div className="flex flex-col gap-2 rounded-lg border border-border bg-background/60 p-3">
						<p className="text-sm font-medium text-foreground">The phrase to keep</p>
						<p className="text-xs leading-relaxed text-muted-foreground">
							Write down or copy this spelling — all lowercase, single spaces. It opens the wallet
							your assets now live in, on any device, every time. The oddly typed version is no
							longer needed.
						</p>
						{keepPhrase ? (
							<Seed seed={keepPhrase} />
						) : (
							<button
								type="button"
								disabled={revealing}
								onClick={revealKeepPhrase}
								className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-violet-500/50 text-sm font-medium text-violet-500 transition-colors hover:bg-violet-500/10 disabled:opacity-60 sm:w-auto sm:px-4"
							>
								{revealing ? <Loader2 className="size-4 animate-spin" /> : null}
								Show my recovery phrase
							</button>
						)}
					</div>
				)}

				{!finished && (
					<button
						type="button"
						disabled={busy || !validTo || nothingLeft}
						onClick={moveEverything}
						className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-violet-600 text-sm font-semibold text-white transition-colors hover:bg-violet-500 disabled:opacity-50 sm:w-auto sm:px-5"
					>
						{busy ? <Loader2 className="size-4 animate-spin" /> : null}
						{busy
							? 'Signing and sending…'
							: btcLeg === 'waiting'
							? 'Move the bitcoin'
							: nothingLeft
							? 'Nothing left to move'
							: 'Move everything'}
						{!busy && !nothingLeft && <ArrowRight className="size-4" />}
					</button>
				)}
			</div>
		</section>
	)
}

function Step({ label, leg }: { label: string; leg: Leg }) {
	return (
		<li className="flex items-center gap-2">
			<span
				className={
					leg === 'done'
						? 'inline-flex size-4 items-center justify-center rounded-full bg-emerald-500 text-white'
						: leg === 'working'
						? 'inline-flex size-4 items-center justify-center text-violet-500'
						: leg === 'error'
						? 'inline-flex size-4 items-center justify-center rounded-full bg-red-500 text-white'
						: 'inline-flex size-4 items-center justify-center rounded-full border border-border'
				}
			>
				{leg === 'done' && <Check className="size-3" />}
				{leg === 'working' && <Loader2 className="size-3 animate-spin" />}
				{leg === 'error' && <X className="size-3" />}
			</span>
			<span className={leg === 'idle' ? 'text-muted-foreground' : 'text-foreground'}>{label}</span>
			{leg === 'waiting' && (
				<span className="text-muted-foreground">— waiting for confirmation</span>
			)}
		</li>
	)
}
